import { useAuthStore } from "@/store/auth";

type WsMessageHandler = (data: unknown) => void;

interface WsClientOptions {
  /** Reconnect after N ms on unexpected close (default: 3000) */
  reconnectDelay?: number;
  /** Maximum reconnect attempts before giving up (default: 10) */
  maxRetries?: number;
}

/**
 * Thin WebSocket client for the data-plane realtime feed.
 * Authenticates via the JWT access token in the query string on handshake.
 * Exposes subscribe/unsubscribe to named event topics.
 */
export class TelemetryWsClient {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, Set<WsMessageHandler>>();
  private retries = 0;
  private readonly opts: Required<WsClientOptions>;
  private closed = false;
  // Only meaningful for a supersuperadmin — see resolve_tenant_id in
  // services/data-plane/src/api/ws.rs. Ignored by the server for every
  // other role. Re-sent on every (re)connect, including after a
  // network-drop reconnect, so the feed doesn't silently fall back to
  // the account the connection started with.
  private accountOverride: string | undefined;
  // Re-sent after every (re)connect for the same reason.
  private lastDeviceIds: string[] | undefined;

  constructor(opts: WsClientOptions = {}) {
    this.opts = {
      reconnectDelay: opts.reconnectDelay ?? 3_000,
      maxRetries: opts.maxRetries ?? 10,
    };
  }

  connect() {
    const token = useAuthStore.getState().accessToken;
    // Relative by default, matching apiClient.ts's fallback to /api/v1 --
    // nginx.conf proxies /ws to data-plane on the same origin, so this
    // works in any environment without a build-time URL. Only set
    // VITE_WS_BASE_URL to point at a different origin than the one
    // serving the frontend itself.
    const base =
      import.meta.env.VITE_WS_BASE_URL ??
      `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}`;
    const params = new URLSearchParams({ token: token ?? "" });
    if (this.accountOverride) {
      params.set("account", this.accountOverride);
    }
    const url = `${base}/ws?${params.toString()}`;

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.retries = 0;
      if (this.lastDeviceIds) {
        this.subscribeDevices(this.lastDeviceIds);
      }
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string) as {
          topic: string;
          payload: unknown;
        };
        this.handlers.get(msg.topic)?.forEach((h) => h(msg.payload));
        // Wildcard handlers receive everything
        this.handlers.get("*")?.forEach((h) => h(msg));
      } catch {
        /* malformed frame — ignore */
      }
    };

    this.ws.onclose = () => {
      if (!this.closed && this.retries < this.opts.maxRetries) {
        this.retries++;
        setTimeout(() => this.connect(), this.opts.reconnectDelay);
      }
    };
  }

  send(data: object) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  /**
   * Narrows this connection's feed to the given device ids — see
   * SubscribeDevicesCommand in services/data-plane/src/api/ws.rs. Pass
   * an empty array to clear the filter (full-tenant feed). Remembered
   * and resent automatically after a reconnect.
   */
  subscribeDevices(deviceIds: string[]) {
    this.lastDeviceIds = deviceIds;
    this.send({ action: "subscribe_devices", device_ids: deviceIds });
  }

  /**
   * Points this connection at a different tenant's feed — only honored
   * by the server for a supersuperadmin (see resolve_tenant_id in
   * services/data-plane/src/api/ws.rs); silently ignored for every
   * other role. A connection is bound to one tenant for its whole
   * lifetime, so changing this forces a reconnect.
   */
  setAccountOverride(accountId: string | undefined) {
    if (this.accountOverride === accountId) return;
    this.accountOverride = accountId;
    if (this.ws) {
      // Detach onclose first so the existing "reconnect after a drop"
      // handler doesn't also fire — connect() below replaces this.ws
      // immediately, so there's nothing for that handler to do anyway.
      this.ws.onclose = null;
      this.ws.close();
      this.connect();
    }
  }

  subscribe(topic: string, handler: WsMessageHandler) {
    if (!this.handlers.has(topic)) {
      this.handlers.set(topic, new Set());
    }
    this.handlers.get(topic)!.add(handler);
  }

  unsubscribe(topic: string, handler: WsMessageHandler) {
    this.handlers.get(topic)?.delete(handler);
  }

  disconnect() {
    this.closed = true;
    this.ws?.close();
  }
}

/** Singleton — import and use across the app */
export const telemetryWs = new TelemetryWsClient();
